import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RecentPostsSidebar from '../components/RecentPostsSidebar';

function Events() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [filteredUpcoming, setFilteredUpcoming] = useState([]);
  const [filteredPast, setFilteredPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedLetter, setSelectedLetter] = useState('');
  
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        axios.get('/api/events/upcoming'),
        axios.get('/api/events/past')
      ]);
      
      setUpcomingEvents(upcomingRes.data);
      setPastEvents(pastRes.data);
      filterEvents(upcomingRes.data, pastRes.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = (upcoming, past) => {
    if (!selectedLetter) {
      setFilteredUpcoming(upcoming);
      setFilteredPast(past);
      return;
    }

    const filterByLetter = (events) => {
      if (selectedLetter === '#') {
        return events.filter(event => {
          const firstChar = event.title.charAt(0).toUpperCase();
          return !/[A-Z]/.test(firstChar);
        });
      }
      return events.filter(event => 
        event.title.charAt(0).toUpperCase() === selectedLetter.toUpperCase()
      );
    };

    setFilteredUpcoming(filterByLetter(upcoming));
    setFilteredPast(filterByLetter(past));
  };

  useEffect(() => {
    filterEvents(upcomingEvents, pastEvents);
  }, [selectedLetter, upcomingEvents, pastEvents]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return { date: dateStr, time: timeStr };
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(selectedLetter === letter ? '' : letter);
  };

  const renderEventCard = (event) => {
    const { date, time } = formatDateTime(event.event_date);
    return (
      <div key={event.id} className="col-xl-4 col-lg-6 col-md-6 col-12">
        <div className="card h-100 tgh-news-card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', transition: 'all 0.3s ease' }}>
          <div className="tgh-card-media">
            <img
              src={event.image ? (event.image.startsWith('/uploads/') ? event.image : `/uploads/${event.image}`) : '/img/Gallery/opd1.jpg'}
              className="card-img-top"
              alt={event.title}
              onError={(e) => {
                e.target.src = '/img/Gallery/opd1.jpg';
              }}
            />
          </div>
          <div className="card-body d-flex flex-column p-4">
            <h5 className="card-title mb-3" style={{ fontSize: '1.15rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.4' }}>
              <Link to={`/events/${event.id}`} className="text-decoration-none" style={{ color: '#1a1a1a' }}>
                {event.title}
              </Link>
            </h5>
            <p className="card-text text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5', textAlign: 'justify' }}>
              {event.description && event.description.length > 120
                ? `${event.description.replace(/<[^>]*>/g, '').substring(0, 120)}...`
                : event.description ? event.description.replace(/<[^>]*>/g, '') : ''}
            </p>
            <div className="mb-3" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-calendar3"></i>
                <span><strong>Date:</strong> {date}</span>
              </div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-clock"></i>
                <span><strong>Time:</strong> {time}</span>
              </div>
              {event.location && (
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-geo-alt"></i>
                  <span><strong>Location:</strong> {event.location}</span>
                </div>
              )}
            </div>
            <Link
              to={`/events/${event.id}`}
              className="btn btn-primary mt-auto"
              style={{
                borderRadius: '4px',
                padding: '0.6rem 1.5rem',
                fontWeight: '500',
                backgroundColor: '#0066cc',
                borderColor: '#0066cc',
                transition: 'all 0.2s'
              }}
            >
              View Details <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(0, 61, 130, 0.9), rgba(0, 61, 130, 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h1 className="text-white mb-0" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
              Hospital Events
            </h1>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              Stay informed about upcoming activities and past events
            </p>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Events
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Events Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">
          <div className="row tgh-page-with-sidebar">
            <div className="col-xl-9 col-lg-8 col-12">
              {/* Tabs */}
              <div className="row mb-4">
                <div className="col-12">
                  <ul className="nav nav-tabs mb-3" style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upcoming')}
                        style={{
                          border: 'none',
                          borderBottom: activeTab === 'upcoming' ? '3px solid #0066cc' : 'none',
                          color: activeTab === 'upcoming' ? '#0066cc' : '#6c757d',
                          fontWeight: activeTab === 'upcoming' ? '600' : '400',
                          backgroundColor: 'transparent',
                          padding: '1rem 2rem',
                          fontSize: '1rem'
                        }}
                      >
                        Upcoming Events ({filteredUpcoming.length || upcomingEvents.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'past' ? 'active' : ''}`}
                        onClick={() => setActiveTab('past')}
                        style={{
                          border: 'none',
                          borderBottom: activeTab === 'past' ? '3px solid #0066cc' : 'none',
                          color: activeTab === 'past' ? '#0066cc' : '#6c757d',
                          fontWeight: activeTab === 'past' ? '600' : '400',
                          backgroundColor: 'transparent',
                          padding: '1rem 2rem',
                          fontSize: '1rem'
                        }}
                      >
                        Past Events ({filteredPast.length || pastEvents.length})
                      </button>
                    </li>
                  </ul>
                  
                  {/* Alphabet Filter */}
                  <div className="mb-3">
                    <label className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333', display: 'block' }}>
                      Find events by first letter
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {alphabet.map((letter) => (
                        <button
                          key={letter}
                          type="button"
                          className={`btn ${selectedLetter === letter ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => handleLetterClick(letter)}
                          style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '50%',
                            fontWeight: '600',
                            fontSize: '1rem',
                            padding: 0
                          }}
                        >
                          {letter}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`btn ${selectedLetter === '#' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleLetterClick('#')}
                        style={{
                          width: '45px',
                          height: '45px',
                          borderRadius: '50%',
                          fontWeight: '600',
                          fontSize: '1rem',
                          padding: 0
                        }}
                      >
                        #
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Events Grid */}
              <div className="row g-4 tgh-card-grid">
                {activeTab === 'upcoming' ? (
                  (filteredUpcoming.length || upcomingEvents.length) === 0 ? (
                    <div className="col-12 text-center py-5">
                      <p className="text-muted" style={{ fontSize: '1rem' }}>No upcoming events scheduled at this time.</p>
                    </div>
                  ) : (
                    (selectedLetter ? filteredUpcoming : upcomingEvents).map(renderEventCard)
                  )
                ) : (
                  (filteredPast.length || pastEvents.length) === 0 ? (
                    <div className="col-12 text-center py-5">
                      <p className="text-muted" style={{ fontSize: '1rem' }}>No past events available.</p>
                    </div>
                  ) : (
                    (selectedLetter ? filteredPast : pastEvents).map(renderEventCard)
                  )
                )}
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4 col-12 tgh-sidebar-col">
              <div style={{ position: 'sticky', top: '20px', zIndex: 10 }}>
                <RecentPostsSidebar type="events" limit={5} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Events;
